import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { AlertTriangle, ShieldAlert, Loader2, CheckCircle2, X } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PROGRESS_STEPS = [
  'Verifying your identity',
  'Deactivating your account',
  'Removing profile from search',
  'Hiding your services & listings',
  'Scheduling messages & files for removal',
  'Finalizing deletion',
];

// step: 'checking' | 'blocked' | 'confirm' | 'otp' | 'deleting' | 'done'
const DeleteAccountModal = ({ onClose, onDeleted }) => {
  const [step, setStep] = useState('checking');
  const [blocker, setBlocker] = useState(null); // {message, blockers}
  const [reason, setReason] = useState('');
  const [confirmText, setConfirmText] = useState('');
  const [otp, setOtp] = useState('');
  const [channelInfo, setChannelInfo] = useState(null); // {channel, masked_target}
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [progressIdx, setProgressIdx] = useState(0);
  const [purgeAt, setPurgeAt] = useState(null);
  const [cooldown, setCooldown] = useState(0);
  const timers = useRef([]);
  const cooldownRef = useRef(null);

  const startCooldown = (secs = 60) => {
    setCooldown(secs);
    if (cooldownRef.current) clearInterval(cooldownRef.current);
    cooldownRef.current = setInterval(() => {
      setCooldown((c) => {
        if (c <= 1) { clearInterval(cooldownRef.current); return 0; }
        return c - 1;
      });
    }, 1000);
  };

  useEffect(() => {
    const scheduledTimers = timers.current;
    (async () => {
      try {
        const res = await axios.get(`${API}/privacy/delete-eligibility`);
        if (res.data.eligible) setStep('confirm');
        else { setBlocker(res.data); setStep('blocked'); }
      } catch (e) {
        setBlocker({ message: 'Unable to check eligibility. Please try again later.' });
        setStep('blocked');
      }
    })();
    return () => { scheduledTimers.forEach(clearTimeout); if (cooldownRef.current) clearInterval(cooldownRef.current); };
  }, []);

  const sendOtp = async () => {
    setErr('');
    if (confirmText !== 'DELETE') { setErr('Please type DELETE to confirm.'); return; }
    setBusy(true);
    try {
      const res = await axios.post(`${API}/privacy/delete/send-otp`, { confirmation: 'DELETE', reason: reason || null });
      setChannelInfo(res.data);
      setStep('otp');
      startCooldown(60);
    } catch (e) {
      setErr(e.response?.data?.detail || 'Could not send verification code.');
    } finally { setBusy(false); }
  };

  const resendOtp = async () => {
    if (cooldown > 0 || busy) return;
    setErr('');
    setBusy(true);
    try {
      const res = await axios.post(`${API}/privacy/delete/send-otp`, { confirmation: 'DELETE', reason: reason || null });
      setChannelInfo(res.data);
      setOtp('');
      startCooldown(60);
    } catch (e) {
      setErr(e.response?.data?.detail || 'Could not resend the code.');
    } finally { setBusy(false); }
  };

  const runProgressAnimation = () => {
    PROGRESS_STEPS.forEach((_, i) => {
      timers.current.push(setTimeout(() => setProgressIdx(i + 1), (i + 1) * 550));
    });
  };

  const confirmDelete = async () => {
    setErr('');
    if (!otp.trim()) { setErr('Enter the verification code.'); return; }
    setBusy(true);
    setStep('deleting');
    const start = Date.now();
    runProgressAnimation();
    try {
      const res = await axios.post(`${API}/privacy/delete/confirm`, {
        otp_code: otp.trim(),
        reason: reason || null,
        device: navigator.userAgent,
      });
      setPurgeAt(res.data.purge_at);
      // Only show "done" once the account is actually deleted AND the progress
      // animation has finished — never before the backend confirms.
      const total = PROGRESS_STEPS.length * 550 + 300;
      const remaining = Math.max(0, total - (Date.now() - start));
      timers.current.push(setTimeout(() => setStep('done'), remaining));
    } catch (e) {
      timers.current.forEach(clearTimeout);
      setProgressIdx(0);
      setErr(e.response?.data?.detail || 'Verification failed.');
      setStep('otp');
      setBusy(false);
    }
  };

  const finish = () => {
    try { localStorage.removeItem('token'); } catch (_) {}
    if (onDeleted) onDeleted();
  };

  const progressPct = Math.round((progressIdx / PROGRESS_STEPS.length) * 100);

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4" data-testid="delete-account-modal">
      <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-6 max-h-[90vh] overflow-y-auto">
        {step !== 'deleting' && step !== 'done' && (
          <button onClick={onClose} className="float-right text-gray-400 hover:text-gray-600" data-testid="close-delete-modal"><X className="w-5 h-5" /></button>
        )}

        {/* CHECKING */}
        {step === 'checking' && (
          <div className="text-center py-10" data-testid="delete-checking">
            <Loader2 className="w-10 h-10 text-gray-400 animate-spin mx-auto mb-3" />
            <p className="text-gray-600">Checking your account…</p>
          </div>
        )}

        {/* BLOCKED */}
        {step === 'blocked' && blocker && (
          <div className="text-center" data-testid="delete-blocked">
            <div className="mx-auto w-16 h-16 bg-amber-100 rounded-full flex items-center justify-center mb-4">
              <ShieldAlert className="w-8 h-8 text-amber-600" />
            </div>
            <h3 className="text-xl font-bold text-gray-900">Deletion unavailable</h3>
            <p className="text-sm text-gray-600 mt-2">{blocker.message}</p>
            {blocker.blockers?.length > 0 && (
              <ul className="text-sm text-gray-700 bg-gray-50 border rounded-lg p-3 mt-3 text-left list-disc list-inside" data-testid="delete-blocker-list">
                {blocker.blockers.map((b, i) => <li key={i}>{b}</li>)}
              </ul>
            )}
            <button onClick={onClose} className="mt-6 w-full py-2.5 bg-gray-100 text-gray-700 rounded-lg font-semibold hover:bg-gray-200" data-testid="blocked-close-btn">
              Close
            </button>
          </div>
        )}

        {/* CONFIRM (type DELETE) */}
        {step === 'confirm' && (
          <div data-testid="delete-confirm-step">
            <div className="text-center mb-5">
              <div className="mx-auto w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mb-4">
                <AlertTriangle className="w-8 h-8 text-red-600" />
              </div>
              <h3 className="text-xl font-bold text-gray-900">Delete your account?</h3>
              <p className="text-sm text-gray-600 mt-2">
                Your account will be locked and scheduled for permanent deletion after a <strong>30-day grace period</strong>. During that time you can email GoodJob Support to restore it.
              </p>
            </div>
            {err && <div className="mb-3 text-sm bg-red-100 text-red-700 rounded-lg px-3 py-2" data-testid="delete-error">{err}</div>}
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Reason for leaving (optional)</label>
                <textarea value={reason} onChange={(e) => setReason(e.target.value)} rows={2}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                  placeholder="Help us improve…" data-testid="delete-reason-input" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Type <strong className="text-red-600">DELETE</strong> to confirm</label>
                <input type="text" value={confirmText} onChange={(e) => setConfirmText(e.target.value)}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                  placeholder="Type DELETE" data-testid="delete-confirm-input" />
              </div>
            </div>
            <div className="flex gap-3 mt-6">
              <button onClick={onClose} className="flex-1 py-2.5 bg-gray-100 text-gray-700 rounded-lg font-semibold hover:bg-gray-200" data-testid="cancel-delete-btn">Cancel</button>
              <button onClick={sendOtp} disabled={confirmText !== 'DELETE' || busy}
                className="flex-1 py-2.5 bg-red-600 text-white rounded-lg font-semibold hover:bg-red-700 disabled:opacity-40 disabled:cursor-not-allowed"
                data-testid="send-otp-btn">
                {busy ? 'Sending…' : 'Send verification code'}
              </button>
            </div>
          </div>
        )}

        {/* OTP */}
        {step === 'otp' && (
          <div data-testid="delete-otp-step">
            <div className="text-center mb-5">
              <div className="mx-auto w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mb-4">
                <AlertTriangle className="w-8 h-8 text-red-600" />
              </div>
              <h3 className="text-xl font-bold text-gray-900">Verify it's you</h3>
              <p className="text-sm text-gray-600 mt-2">
                We sent a 6-digit code via {channelInfo?.channel === 'sms' ? 'SMS' : 'email'} to <strong>{channelInfo?.masked_target}</strong>. Enter it to confirm deletion — this ensures only you can delete your account.
              </p>
            </div>
            {err && <div className="mb-3 text-sm bg-red-100 text-red-700 rounded-lg px-3 py-2" data-testid="delete-error">{err}</div>}
            <input type="text" inputMode="numeric" value={otp} onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
              className="w-full px-3 py-3 border rounded-lg text-center text-2xl tracking-[0.4em] focus:ring-2 focus:ring-red-500"
              placeholder="000000" data-testid="delete-otp-input" />
            <div className="text-center mt-3">
              <button
                onClick={resendOtp}
                disabled={cooldown > 0 || busy}
                className="text-sm font-medium text-red-600 hover:text-red-700 disabled:text-gray-400 disabled:cursor-not-allowed"
                data-testid="resend-otp-btn"
              >
                {cooldown > 0 ? `Resend code in ${cooldown}s` : "Didn't get it? Resend code"}
              </button>
            </div>
            <div className="flex gap-3 mt-6">
              <button onClick={() => setStep('confirm')} className="flex-1 py-2.5 bg-gray-100 text-gray-700 rounded-lg font-semibold hover:bg-gray-200" data-testid="otp-back-btn">Back</button>
              <button onClick={confirmDelete} disabled={busy || otp.length < 4}
                className="flex-1 py-2.5 bg-red-600 text-white rounded-lg font-semibold hover:bg-red-700 disabled:opacity-40"
                data-testid="confirm-delete-btn">
                {busy ? 'Verifying…' : 'Confirm deletion'}
              </button>
            </div>
          </div>
        )}

        {/* DELETING PROGRESS */}
        {step === 'deleting' && (
          <div className="py-4" data-testid="delete-progress">
            <h3 className="text-lg font-bold text-gray-900 text-center mb-4">Deleting your account…</h3>
            <div className="w-full bg-gray-200 rounded-full h-3 overflow-hidden mb-2">
              <div className="bg-red-600 h-3 rounded-full transition-all duration-500" style={{ width: `${progressPct}%` }} data-testid="delete-progress-bar" />
            </div>
            <p className="text-center text-sm font-semibold text-gray-700 mb-4" data-testid="delete-progress-pct">{progressPct}%</p>
            <ul className="space-y-2">
              {PROGRESS_STEPS.map((s, i) => (
                <li key={i} className="flex items-center gap-2 text-sm">
                  {i < progressIdx
                    ? <CheckCircle2 className="w-4 h-4 text-green-600" />
                    : i === progressIdx
                      ? <Loader2 className="w-4 h-4 text-red-600 animate-spin" />
                      : <span className="w-4 h-4 rounded-full border border-gray-300 inline-block" />}
                  <span className={i <= progressIdx ? 'text-gray-800' : 'text-gray-400'}>{s}</span>
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* DONE */}
        {step === 'done' && (
          <div className="text-center py-4" data-testid="delete-done">
            <div className="mx-auto w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mb-4">
              <CheckCircle2 className="w-9 h-9 text-green-600" />
            </div>
            <h3 className="text-xl font-bold text-gray-900">Account scheduled for deletion</h3>
            <p className="text-sm text-gray-600 mt-2">
              Your account has been deactivated. It will be permanently deleted
              {purgeAt ? <> on <strong>{new Date(purgeAt).toLocaleDateString()}</strong></> : ' after 30 days'}.
              To restore it before then, email GoodJob Support.
            </p>
            <button onClick={finish} className="mt-6 w-full py-2.5 bg-gray-900 text-white rounded-lg font-semibold hover:bg-black" data-testid="delete-done-btn">
              Log out
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default DeleteAccountModal;
